import React from 'react';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import {
  PowerIcon,
  TurboIcon,
  LightIcon,
  FanIcon,
  ClapetIcon,
  BurnerIcon,
} from '../../../theme/icons';
import { ActionExtendedValue } from '../../../redux/controlUnit/selectors/actions';
import Status from '../DisplayValues/StatusOven';
import { useCurrentStateByControlUnitId } from '../../../redux/controlUnit/hooks/useCurrentStateByControlUnitId';
import { getPolyglot } from '../../../i18n';
import ModeOven from '../DisplayValues/ModeOven';

interface DeckActionsStatusProps {
  deviceId: string;
  controlUnitId: string;
  on?: ActionExtendedValue;
  light?: ActionExtendedValue;
  clapet?: ActionExtendedValue;
  burner?: ActionExtendedValue;
  multiFunction?: ActionExtendedValue;
  fan?: ActionExtendedValue;
  CUTranslate: ReturnType<typeof useCurrentStateByControlUnitId>['CUTranslate'];
  isDeviceConnected?: boolean;
  isDeviceOn?: boolean;
}

function DeckActionsStatus({
  deviceId,
  controlUnitId,
  on,
  light,
  clapet,
  burner,
  multiFunction,
  fan,
  isDeviceConnected,
  isDeviceOn,
  CUTranslate,
}: DeckActionsStatusProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const polyglot = getPolyglot();
  return (
    <Stack
      justifyContent={isMobile ? 'center' : 'flex-end'}
      flexWrap={'wrap'}
      direction={'row'}
      justifyItems={'center'}
      alignItems={'center'}
      gap={2}
    >
      {on && (
        <Status
          value={on?.value}
          icon={<PowerIcon height={'1em'} />}
          name={
            !isDeviceConnected
              ? polyglot.t('composable_device.power')
              : on?.value
              ? polyglot.t('composable_device.power_on')
              : polyglot.t('composable_device.power_off')
          }
          deviceId={deviceId}
          controlUnitId={controlUnitId}
          actionId={on?.metadata.id}
          CUTranslate={CUTranslate}
          isDeviceConnected={isDeviceConnected}
          isDeviceOn={on?.value}
        />
      )}
      {light && (
        <Status
          value={light?.value}
          icon={<LightIcon height={'1em'} />}
          name={
            !isDeviceConnected
              ? polyglot.t('composable_device.light')
              : light?.value
              ? polyglot.t('composable_device.light_on')
              : polyglot.t('composable_device.light_off')
          }
          deviceId={deviceId}
          controlUnitId={controlUnitId}
          actionId={light?.metadata.id}
          CUTranslate={CUTranslate}
          isDeviceConnected={isDeviceConnected}
          isDeviceOn={isDeviceOn}
        />
      )}
      {fan && (
        <Status
          value={fan?.value}
          icon={<FanIcon height={'1em'} />}
          name={
            !isDeviceConnected
              ? polyglot.t('composable_device.fan')
              : fan?.value
              ? polyglot.t('composable_device.fan_on')
              : polyglot.t('composable_device.fan_off')
          }
          deviceId={deviceId}
          controlUnitId={controlUnitId}
          actionId={fan?.metadata.id}
          CUTranslate={CUTranslate}
          isDeviceConnected={isDeviceConnected}
          isDeviceOn={isDeviceOn}
        />
      )}
      {clapet && (
        <Status
          value={clapet?.value}
          icon={<ClapetIcon height={'1em'} />}
          name={
            !isDeviceConnected
              ? polyglot.t('composable_device.clapet')
              : clapet?.value
              ? polyglot.t('composable_device.clapet_on')
              : polyglot.t('composable_device.clapet_off')
          }
          deviceId={deviceId}
          controlUnitId={controlUnitId}
          actionId={clapet?.metadata.id}
          CUTranslate={CUTranslate}
          isDeviceConnected={isDeviceConnected}
          isDeviceOn={isDeviceOn}
        />
      )}
      {!isMobile && (burner || multiFunction) && (
        <Box
          height={'50px'}
          borderRight={(theme) => `2px solid ${theme.palette.divider}`}
        ></Box>
      )}
      {(burner || multiFunction) && (
        <Box>
          <Typography
            fontSize={'0.9rem'}
            textAlign="start"
            sx={{ opacity: isDeviceOn && isDeviceConnected ? 1 : 0.5 }}
          >
            {polyglot.t('composable_device.mode')}
          </Typography>
          <Box display="flex" flexDirection="row" alignItems="center">
            {burner && (
              <ModeOven
                value={burner?.value}
                icon={
                  <BurnerIcon
                    height={'1em'}
                    color={
                      isDeviceConnected && isDeviceOn
                        ? theme.palette.primary.main
                        : theme.palette.grey[500]
                    }
                  />
                }
                name={polyglot.t('composable_device.burner')}
                deviceId={deviceId}
                controlUnitId={controlUnitId}
                actionId={burner?.metadata.id}
                CUTranslate={CUTranslate}
                isDeviceConnected={isDeviceConnected}
                isDeviceOn={isDeviceOn}
              />
            )}
            {multiFunction && (
              <ModeOven
                value={multiFunction?.value}
                icon={
                  <TurboIcon
                    height={'1em'}
                    color={
                      isDeviceConnected && isDeviceOn
                        ? theme.palette.primary.main
                        : theme.palette.grey[500]
                    }
                  />
                }
                name={polyglot.t('composable_device.turbo')}
                deviceId={deviceId}
                controlUnitId={controlUnitId}
                actionId={multiFunction?.metadata.id}
                CUTranslate={CUTranslate}
                isDeviceConnected={isDeviceConnected}
                isDeviceOn={isDeviceOn}
              />
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );
}

export default DeckActionsStatus;
